import type { LDNDStatusEvent } from '@avisoma/shared';
import { Badge } from '../../../catalyst/badge';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

/** `enginePosition` (DR-6299): the engine an AMOS zone task card belongs to. */
type LDNDRow = LDNDStatusEvent & { pageId?: string; ldndPageIds?: string[]; enginePosition?: '1' | '2' };

export function LDNDTable<T extends LDNDRow>({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: T[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: T, index: number) => void;
  rowFindings?: RowFindings;
}) {
  if (rows.length === 0) return null;
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>Reference</Th>
          <Th>Task</Th>
          <Th>Compliance</Th>
          <Th>Next due</Th>
          <Th>Repeat</Th>
          <Th>Flag</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePagesCell row={r} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">
              {r.ldndReference}
              {r.enginePosition && (
                <span className="ml-2 font-sans">
                  <Badge color="zinc">Engine {r.enginePosition}</Badge>
                </span>
              )}
            </Td>
            <TruncatedTd value={r.taskDescription} />
            <Td>
              {joinUnits([
                r.complianceDate,
                r.complianceTsn != null ? `TSN ${r.complianceTsn}` : undefined,
                r.complianceCsn != null ? `CSN ${r.complianceCsn}` : undefined,
                r.complianceShopVisit,
              ])}
            </Td>
            <Td>
              {joinUnits([
                r.nextDueDate,
                r.nextDueHours != null ? `${r.nextDueHours} hrs` : undefined,
                r.nextDueCycles != null ? `${r.nextDueCycles} cyc` : undefined,
                r.nextDueShopVisit ?? (r.nextDueAtShopVisit ? 'shop visit' : undefined),
              ])}
            </Td>
            <Td>
              {joinUnits([
                r.repeatIntervalHours != null ? `${r.repeatIntervalHours} hrs` : undefined,
                r.repeatIntervalCycles != null ? `${r.repeatIntervalCycles} cyc` : undefined,
                r.repeatIntervalDays != null ? `${r.repeatIntervalDays} days` : undefined,
                r.repeatIntervalShopVisits != null ? `${r.repeatIntervalShopVisits} sv` : undefined,
              ])}
            </Td>
            <Td>
              {r.notApplicable || r.unknownFlag ? (
                <span className="flex items-center gap-1">
                  {r.notApplicable && (
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200"
                      title="Task marked not applicable in the source document"
                    >
                      N/A
                    </span>
                  )}
                  {r.unknownFlag && (
                    <span className="text-amber-600 dark:text-amber-400" title="Needs manual review">
                      ⚠
                    </span>
                  )}
                </span>
              ) : (
                '—'
              )}
            </Td>
            <TruncatedTd value={r.remarks} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function joinUnits(parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' · ') || '—';
}

function SourcePagesCell({
  row,
  pageIdToPosition,
}: {
  row: LDNDRow;
  pageIdToPosition: Map<string, number>;
}) {
  const pageIds = row.ldndPageIds ?? (row.pageId ? [row.pageId] : []);
  if (pageIds.length === 0) return <>—</>;
  return (
    <span className="flex flex-col gap-0.5">
      {pageIds.map((pageId) => (
        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
      ))}
    </span>
  );
}
